/**
 * Frontend runtime for the Carousel Slides viewport.
 */

import EmblaCarousel from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import AutoHeight from 'embla-carousel-auto-height';
import AutoScroll from 'embla-carousel-auto-scroll';
import Fade from 'embla-carousel-fade';

import {
	capSlidesToMinWidth,
	resolveResponsiveSettings,
	sortBreakpoints,
} from './responsive';

function getL10n() {
	return window.ollieCarouselL10n || {};
}

// Sequence for generated viewport ids (scrollbar aria-controls).
let viewportIdCounter = 0;

function sprintfNamed( template, ...values ) {
	return String( template ).replace( /%(\d+)\$[sd]/g, ( _, number ) => {
		const value = values[ Number( number ) - 1 ];
		return value !== null && value !== undefined ? String( value ) : '';
	} );
}

export function parseBreakpoints( value ) {
	try {
		const parsed = JSON.parse( value || '[]' );
		return Array.isArray( parsed ) ? sortBreakpoints( parsed ) : [];
	} catch ( error ) {
		return [];
	}
}

export function getBoundaryElements( boundary, selector ) {
	return Array.from( boundary.querySelectorAll( selector ) ).filter(
		( element ) => element.closest( '.ollie-carousel-block' ) === boundary
	);
}

function applySlideStyles( viewport, slidesPerView ) {
	viewport.style.setProperty(
		'--ollie-slides-per-view',
		String( slidesPerView )
	);
}

/**
 * Whether the auto-height plugin should be active.
 *
 * AutoHeight sets the container height from the SELECTED snap group's slides
 * only (Embla's slideRegistry) — with several slides per view that clips
 * taller visible neighbours on load. Single-slide views are its intended use;
 * multi-slide views size naturally to the tallest slide instead.
 *
 * @param {boolean} useFade       Whether the fade plugin is active.
 * @param {number}  slidesPerView Effective slides per view.
 * @return {boolean} Whether auto height applies.
 */
export function shouldUseAutoHeight( useFade, slidesPerView ) {
	return ! useFade && slidesPerView === 1;
}

export function setupResponsiveSettings( {
	embla,
	viewport,
	baseSettings,
	breakpoints,
	createPlugins,
	initialSettings,
	initialFade,
	hasFade,
	capSettings = ( settings ) => settings,
	watchResize = false,
	onSettingsChange = () => {},
} ) {
	let currentSettings = initialSettings;
	let currentFade = initialFade;
	let currentAutoHeight = shouldUseAutoHeight(
		initialFade,
		initialSettings.slidesPerView
	);

	const applyResponsiveSettings = () => {
		const nextSettings = capSettings(
			resolveResponsiveSettings(
				baseSettings,
				breakpoints,
				window.innerWidth
			)
		);
		const nextFade = hasFade && nextSettings.slidesPerView === 1;
		const nextAutoHeight = shouldUseAutoHeight(
			nextFade,
			nextSettings.slidesPerView
		);
		const settingsChanged =
			nextSettings.slidesPerView !== currentSettings.slidesPerView ||
			nextSettings.slidesPerGroup !== currentSettings.slidesPerGroup;
		const pluginsChanged =
			nextFade !== currentFade || nextAutoHeight !== currentAutoHeight;

		applySlideStyles( viewport, nextSettings.slidesPerView );
		viewport.classList.toggle(
			'ollie-carousel-auto-height',
			nextAutoHeight
		);

		if ( ! settingsChanged && ! pluginsChanged ) {
			return;
		}

		currentSettings = nextSettings;
		currentFade = nextFade;
		currentAutoHeight = nextAutoHeight;
		onSettingsChange( nextSettings );
		embla.reInit(
			{ slidesToScroll: nextSettings.slidesPerGroup },
			pluginsChanged
				? createPlugins( nextFade, nextSettings.slidesPerView )
				: undefined
		);
	};

	const listeners = breakpoints.map( ( breakpoint ) => {
		const mediaQuery = window.matchMedia(
			`(max-width: ${ breakpoint.width }px)`
		);
		mediaQuery.addEventListener( 'change', applyResponsiveSettings );
		return { mediaQuery, listener: applyResponsiveSettings };
	} );

	// Breakpoint listeners only fire at their thresholds; a minimum slide
	// width depends on the carousel's continuous width, so it needs a
	// (debounced) resize listener too.
	let resizeTimer = 0;
	const onResize = () => {
		window.clearTimeout( resizeTimer );
		resizeTimer = window.setTimeout( applyResponsiveSettings, 150 );
	};
	if ( watchResize ) {
		window.addEventListener( 'resize', onResize );
	}

	applyResponsiveSettings();

	return () => {
		listeners.forEach( ( { mediaQuery, listener } ) => {
			mediaQuery.removeEventListener( 'change', listener );
		} );
		if ( watchResize ) {
			window.clearTimeout( resizeTimer );
			window.removeEventListener( 'resize', onResize );
		}
	};
}

function setupNavigation( embla, prevButtons, nextButtons ) {
	const cleanups = [];

	prevButtons.forEach( ( button ) => {
		const onClick = () => embla.scrollPrev();
		button.addEventListener( 'click', onClick );
		cleanups.push( () => button.removeEventListener( 'click', onClick ) );
	} );

	nextButtons.forEach( ( button ) => {
		const onClick = () => embla.scrollNext();
		button.addEventListener( 'click', onClick );
		cleanups.push( () => button.removeEventListener( 'click', onClick ) );
	} );

	const updateState = () => {
		const canScrollPrev = embla.canScrollPrev();
		const canScrollNext = embla.canScrollNext();
		prevButtons.forEach( ( button ) => {
			button.disabled = ! canScrollPrev;
		} );
		nextButtons.forEach( ( button ) => {
			button.disabled = ! canScrollNext;
		} );
	};

	embla.on( 'select', updateState );
	embla.on( 'reInit', updateState );
	updateState();

	return () => {
		embla.off( 'select', updateState );
		embla.off( 'reInit', updateState );
		cleanups.forEach( ( cleanup ) => cleanup() );
	};
}

export function setupKeyboardNavigation( embla, viewport, isRtl ) {
	const hadTabindex = viewport.hasAttribute( 'tabindex' );
	const previousTabindex = viewport.getAttribute( 'tabindex' );
	viewport.setAttribute( 'tabindex', '0' );

	const onKeydown = ( event ) => {
		// Only this viewport's own keys drive it: ignore keys bubbling up
		// from a nested carousel, and from interactive slide content where
		// arrow keys have their own meaning (text fields, media).
		const target = event.target;
		if ( target !== viewport && target instanceof Element ) {
			if ( target.closest( '.ollie-carousel-viewport' ) !== viewport ) {
				return;
			}
			if (
				target.closest(
					'input, textarea, select, video, audio, [contenteditable]:not([contenteditable="false"])'
				)
			) {
				return;
			}
		}

		const previous =
			( event.key === 'ArrowLeft' && ! isRtl ) ||
			( event.key === 'ArrowRight' && isRtl );
		const next =
			( event.key === 'ArrowRight' && ! isRtl ) ||
			( event.key === 'ArrowLeft' && isRtl );

		if ( previous ) {
			embla.scrollPrev();
			event.preventDefault();
		} else if ( next ) {
			embla.scrollNext();
			event.preventDefault();
		}
	};

	viewport.addEventListener( 'keydown', onKeydown );

	return () => {
		viewport.removeEventListener( 'keydown', onKeydown );
		if ( hadTabindex ) {
			viewport.setAttribute( 'tabindex', previousTabindex );
		} else {
			viewport.removeAttribute( 'tabindex' );
		}
	};
}

/**
 * The autoplay and auto-scroll plugins expose the same play/stop/isPlaying
 * API under different keys; a carousel only ever runs one of them.
 *
 * @param {Object} embla Embla instance.
 * @return {Object|undefined} The active motion plugin, if any.
 */
function getMotionPlugin( embla ) {
	const plugins = embla.plugins() || {};
	return plugins.autoplay || plugins.autoScroll;
}

/**
 * Make an interaction-stop of stepped autoplay survive reInit.
 *
 * Autoplay runs with stopOnInteraction: a swipe stops it permanently. But
 * every reInit (responsive breakpoint, image settling, Embla's own resize
 * handling) re-creates plugins with playOnInit, silently restarting them.
 * Recording the interaction in the shared state lets reInit re-stop, the
 * same way an explicit toggle-button pause persists. Auto-scroll is exempt:
 * it intentionally resumes after interaction.
 *
 * @param {Object} embla Embla instance.
 * @param {Object} state Shared autoplay state ({ userPaused }).
 * @return {Function} Cleanup.
 */
export function setupInteractionPause( embla, state ) {
	const onPointerDown = () => {
		if ( embla.plugins()?.autoplay ) {
			state.userPaused = true;
		}
	};
	const onReInit = () => {
		if ( state.userPaused ) {
			getMotionPlugin( embla )?.stop();
		}
	};

	embla.on( 'pointerDown', onPointerDown );
	embla.on( 'reInit', onReInit );

	return () => {
		embla.off( 'pointerDown', onPointerDown );
		embla.off( 'reInit', onReInit );
	};
}

export function setupAutoplayToggle(
	embla,
	boundary,
	state = { userPaused: false }
) {
	const l10n = getL10n();
	// Only user-placed Pause nav buttons are wired up; nothing is injected.
	const placedButtons = getBoundaryElements(
		boundary,
		'.ollie-carousel-autoplay-toggle'
	);
	if ( ! placedButtons.length ) {
		return () => {};
	}

	// Hide the whole nav wrapper (not just the button) so no empty
	// container is left behind.
	const setHidden = ( hiddenState ) => {
		placedButtons.forEach( ( toggle ) => {
			const wrapper =
				toggle.closest( '.ollie-carousel-nav--autoplay' ) || toggle;
			wrapper.hidden = hiddenState;
		} );
	};

	const renderState = ( isPlaying ) => {
		const label = isPlaying
			? l10n.pause || 'Pause autoplay'
			: l10n.play || 'Play autoplay';
		// Per the APG carousel pattern, a rotation control with a swapping
		// accessible name must not also expose aria-pressed — the combination
		// reads as contradictory state ("Play …, pressed").
		placedButtons.forEach( ( toggle ) => {
			toggle.setAttribute( 'aria-label', label );
			toggle.textContent = label;
			toggle.classList.toggle( 'is-paused', ! isPlaying );
		} );
	};

	const onClick = () => {
		const autoplay = getMotionPlugin( embla );
		if ( ! autoplay || embla.scrollSnapList().length <= 1 ) {
			return;
		}
		if ( autoplay.isPlaying() ) {
			state.userPaused = true;
			autoplay.stop();
			renderState( false );
		} else {
			state.userPaused = false;
			autoplay.play();
			renderState( true );
		}
	};

	const reconcile = () => {
		if ( embla.scrollSnapList().length <= 1 ) {
			setHidden( true );
			return;
		}
		setHidden( false );

		const autoplay = getMotionPlugin( embla );
		if ( state.userPaused && autoplay?.isPlaying() ) {
			autoplay.stop();
		}
		renderState( Boolean( autoplay?.isPlaying() ) );
	};

	const onPlay = () => {
		// Auto-scroll restarts itself after a drag; an explicit pause from
		// the toggle button must survive that.
		if ( state.userPaused ) {
			getMotionPlugin( embla )?.stop();
			renderState( false );
			return;
		}
		renderState( true );
	};
	const onStop = () => renderState( false );

	placedButtons.forEach( ( toggle ) =>
		toggle.addEventListener( 'click', onClick )
	);
	embla.on( 'autoplay:play', onPlay );
	embla.on( 'autoplay:stop', onStop );
	embla.on( 'autoScroll:play', onPlay );
	embla.on( 'autoScroll:stop', onStop );
	embla.on( 'reInit', reconcile );
	reconcile();

	return () => {
		placedButtons.forEach( ( toggle ) =>
			toggle.removeEventListener( 'click', onClick )
		);
		embla.off( 'autoplay:play', onPlay );
		embla.off( 'autoplay:stop', onStop );
		embla.off( 'autoScroll:play', onPlay );
		embla.off( 'autoScroll:stop', onStop );
		embla.off( 'reInit', reconcile );
	};
}

export function setupPauseOnHover( embla, viewport, state ) {
	let hoverPaused = false;
	let interactionStopped = false;

	const stopForHover = () => {
		const autoplay = getMotionPlugin( embla );
		if ( embla.scrollSnapList().length > 1 && autoplay?.isPlaying() ) {
			hoverPaused = true;
			interactionStopped = false;
			autoplay.stop();
		}
	};

	const resumeAfterHover = () => {
		if ( ! hoverPaused ) {
			return;
		}
		const shouldResume = ! interactionStopped && ! state.userPaused;
		hoverPaused = false;
		interactionStopped = false;
		const autoplay = getMotionPlugin( embla );
		if ( shouldResume && embla.scrollSnapList().length > 1 && autoplay ) {
			autoplay.play();
		}
	};

	const preserveHoverPause = () => {
		const autoplay = getMotionPlugin( embla );
		if ( hoverPaused && autoplay?.isPlaying() ) {
			autoplay.stop();
		}
	};

	const recordInteractionStop = () => {
		if ( hoverPaused ) {
			interactionStopped = true;
		}
	};

	// Auto-scroll restarts itself after a drag settles and on focusout,
	// regardless of prior state — an active hover pause has to win.
	const onMotionPlay = () => {
		if ( hoverPaused ) {
			getMotionPlugin( embla )?.stop();
		}
	};

	viewport.addEventListener( 'mouseenter', stopForHover );
	viewport.addEventListener( 'mouseleave', resumeAfterHover );
	embla.on( 'pointerDown', recordInteractionStop );
	embla.on( 'slideFocusStart', recordInteractionStop );
	embla.on( 'reInit', preserveHoverPause );
	embla.on( 'autoplay:play', onMotionPlay );
	embla.on( 'autoScroll:play', onMotionPlay );

	return () => {
		viewport.removeEventListener( 'mouseenter', stopForHover );
		viewport.removeEventListener( 'mouseleave', resumeAfterHover );
		embla.off( 'pointerDown', recordInteractionStop );
		embla.off( 'slideFocusStart', recordInteractionStop );
		embla.off( 'reInit', preserveHoverPause );
		embla.off( 'autoplay:play', onMotionPlay );
		embla.off( 'autoScroll:play', onMotionPlay );
	};
}

export function setupPagination( embla, container ) {
	const l10n = getL10n();
	let dotCleanups = [];

	const removeDots = () => {
		dotCleanups.forEach( ( cleanup ) => cleanup() );
		dotCleanups = [];
	};

	const buildDots = () => {
		const scrollSnaps = embla.scrollSnapList();
		// Embla reInits on any width change; when the snap count is stable
		// (the common case) refresh state in place instead of rebuilding.
		if (
			container.querySelectorAll( '.ollie-carousel-dot' ).length ===
				scrollSnaps.length &&
			scrollSnaps.length > 0
		) {
			updateDots();
			return;
		}

		const activeIndex = embla.selectedScrollSnap();
		const hadFocus = container.contains(
			container.ownerDocument.activeElement
		);
		removeDots();
		container.innerHTML = '';

		scrollSnaps.forEach( ( _, index ) => {
			const dot = container.ownerDocument.createElement( 'button' );
			dot.className = 'ollie-carousel-dot';
			dot.type = 'button';
			dot.setAttribute(
				'aria-label',
				sprintfNamed(
					l10n.goToSlide || 'Go to slide %1$d of %2$d',
					index + 1,
					scrollSnaps.length
				)
			);
			if ( index === activeIndex ) {
				dot.classList.add( 'is-active' );
				dot.setAttribute( 'aria-current', 'true' );
			}
			const onClick = () => embla.scrollTo( index );
			dot.addEventListener( 'click', onClick );
			dotCleanups.push( () => {
				dot.removeEventListener( 'click', onClick );
				dot.remove();
			} );
			container.appendChild( dot );
		} );

		if ( hadFocus ) {
			const restore =
				container.querySelector( '.ollie-carousel-dot.is-active' ) ||
				container.querySelector( '.ollie-carousel-dot' );
			restore?.focus( { preventScroll: true } );
		}
	};

	const updateDots = () => {
		const selected = embla.selectedScrollSnap();
		container
			.querySelectorAll( '.ollie-carousel-dot' )
			.forEach( ( dot, index ) => {
				const active = index === selected;
				dot.classList.toggle( 'is-active', active );
				if ( active ) {
					dot.setAttribute( 'aria-current', 'true' );
				} else {
					dot.removeAttribute( 'aria-current' );
				}
			} );
	};

	embla.on( 'reInit', buildDots );
	embla.on( 'select', updateDots );
	buildDots();

	return () => {
		embla.off( 'reInit', buildDots );
		embla.off( 'select', updateDots );
		removeDots();
	};
}

export function setupScrollbar( embla, scrollbar ) {
	const track = scrollbar.querySelector( '.ollie-carousel-scrollbar__track' );
	const thumb = scrollbar.querySelector( '.ollie-carousel-scrollbar__thumb' );
	if ( ! track || ! thumb ) {
		return () => {};
	}

	const l10n = getL10n();
	const computeRtl = () =>
		scrollbar
			.closest( '.ollie-carousel-block' )
			?.classList.contains( 'ollie-carousel-rtl' ) || false;
	// The scroll handler runs every animation frame while a marquee plays;
	// cache the RTL DOM walk and refresh it on reInit.
	let isRtl = computeRtl();

	track.setAttribute( 'role', 'scrollbar' );
	track.setAttribute( 'aria-orientation', 'horizontal' );
	track.setAttribute( 'aria-label', l10n.scrollbar || 'Carousel scroll bar' );
	track.setAttribute( 'aria-valuemin', '0' );
	track.setAttribute( 'aria-valuemax', '100' );
	track.setAttribute( 'tabindex', '0' );

	// role="scrollbar" requires aria-controls naming the scrolled element.
	const viewport = embla.rootNode?.();
	if ( viewport ) {
		if ( ! viewport.id ) {
			viewportIdCounter += 1;
			viewport.id = `ollie-carousel-viewport-${ viewportIdCounter }`;
		}
		track.setAttribute( 'aria-controls', viewport.id );
	}

	let lastValueNow = null;

	const update = () => {
		const snapCount = embla.scrollSnapList().length;
		scrollbar.classList.toggle( 'is-hidden', snapCount <= 1 );
		if ( snapCount <= 1 ) {
			return;
		}

		// Thumb spans the visible fraction; its travel covers the remainder.
		const width = 100 / snapCount;
		const progress = Math.min( 1, Math.max( 0, embla.scrollProgress() ) );
		const offset = progress * ( 100 - width );

		thumb.style.width = `${ width }%`;
		if ( isRtl ) {
			thumb.style.left = 'auto';
			thumb.style.right = `${ offset }%`;
		} else {
			thumb.style.right = 'auto';
			thumb.style.left = `${ offset }%`;
		}
		const valueNow = String( Math.round( progress * 100 ) );
		if ( valueNow !== lastValueNow ) {
			lastValueNow = valueNow;
			track.setAttribute( 'aria-valuenow', valueNow );
		}
	};

	// Map a pointer position to a scroll snap and go there. The thumb's
	// center follows the pointer within the thumb's travel range. During a
	// drag the track geometry is static, so the rect is snapshotted once.
	let dragRect = null;

	const scrollToPointer = ( event ) => {
		const snapCount = embla.scrollSnapList().length;
		if ( snapCount <= 1 ) {
			return;
		}

		const rect = dragRect || track.getBoundingClientRect();
		if ( ! rect.width ) {
			return;
		}

		const thumbFraction = 1 / snapCount;
		let position = ( event.clientX - rect.left ) / rect.width;
		if ( isRtl ) {
			position = 1 - position;
		}
		const progress = Math.min(
			1,
			Math.max(
				0,
				( position - thumbFraction / 2 ) / ( 1 - thumbFraction )
			)
		);

		embla.scrollTo( Math.round( progress * ( snapCount - 1 ) ) );
	};

	let dragging = false;

	const onPointerDown = ( event ) => {
		dragging = true;
		dragRect = track.getBoundingClientRect();
		scrollbar.classList.add( 'is-dragging' );
		track.setPointerCapture?.( event.pointerId );
		scrollToPointer( event );
		event.preventDefault();
	};

	const onPointerMove = ( event ) => {
		if ( dragging ) {
			scrollToPointer( event );
		}
	};

	const endDrag = ( event ) => {
		if ( ! dragging ) {
			return;
		}
		dragging = false;
		dragRect = null;
		scrollbar.classList.remove( 'is-dragging' );
		track.releasePointerCapture?.( event.pointerId );
	};

	const onKeydown = ( event ) => {
		const forward =
			( 'ArrowRight' === event.key && ! isRtl ) ||
			( 'ArrowLeft' === event.key && isRtl );
		const backward =
			( 'ArrowLeft' === event.key && ! isRtl ) ||
			( 'ArrowRight' === event.key && isRtl );

		if ( forward ) {
			embla.scrollNext();
			event.preventDefault();
		} else if ( backward ) {
			embla.scrollPrev();
			event.preventDefault();
		}
	};

	track.addEventListener( 'pointerdown', onPointerDown );
	track.addEventListener( 'pointermove', onPointerMove );
	track.addEventListener( 'pointerup', endDrag );
	track.addEventListener( 'pointercancel', endDrag );
	track.addEventListener( 'keydown', onKeydown );

	const onReInit = () => {
		isRtl = computeRtl();
		update();
	};

	embla.on( 'scroll', update );
	embla.on( 'reInit', onReInit );
	update();

	return () => {
		track.removeEventListener( 'pointerdown', onPointerDown );
		track.removeEventListener( 'pointermove', onPointerMove );
		track.removeEventListener( 'pointerup', endDrag );
		track.removeEventListener( 'pointercancel', endDrag );
		track.removeEventListener( 'keydown', onKeydown );
		embla.off( 'scroll', update );
		embla.off( 'reInit', onReInit );
	};
}

export function setupSlideAccessibility( embla, viewport, getSlidesPerView ) {
	const managedAttributes = [
		'inert',
		'aria-hidden',
		'role',
		'aria-label',
		'aria-roledescription',
	];
	// Keyed by slide element so slides added after init (e.g. content swapped
	// in before a reInit) are picked up and can still be restored on cleanup.
	const originalAttributes = new Map();
	let slides = [];

	const refreshSlides = () => {
		slides = Array.from(
			viewport.querySelectorAll( '.ollie-carousel-slide' )
		).filter(
			( slide ) =>
				slide.closest( '.ollie-carousel-viewport' ) === viewport
		);
		slides.forEach( ( slide ) => {
			if ( ! originalAttributes.has( slide ) ) {
				originalAttributes.set(
					slide,
					Object.fromEntries(
						managedAttributes.map( ( name ) => [
							name,
							{
								present: slide.hasAttribute( name ),
								value: slide.getAttribute( name ),
							},
						] )
					)
				);
			}
		} );
		// Drop entries for slides removed from the DOM (AJAX content swaps)
		// so detached nodes aren't retained for the carousel's lifetime.
		// Slides still in the current list are kept even when the whole
		// carousel is (temporarily) detached — their originals must survive
		// for restore-on-teardown.
		originalAttributes.forEach( ( _, slide ) => {
			if ( ! slide.isConnected && ! slides.includes( slide ) ) {
				originalAttributes.delete( slide );
			}
		} );
	};
	refreshSlides();
	const l10n = getL10n();

	const setAttributeIfChanged = ( slide, name, value ) => {
		if ( slide.getAttribute( name ) !== value ) {
			slide.setAttribute( name, value );
		}
	};

	const update = () => {
		const selectedIndex = embla.selectedScrollSnap();
		const inView = embla.slidesInView();
		slides.forEach( ( slide, index ) => {
			const visible =
				inView.length > 0
					? inView.includes( index )
					: index >= selectedIndex &&
					  index < selectedIndex + getSlidesPerView();
			if ( visible ) {
				slide.removeAttribute( 'aria-hidden' );
				slide.removeAttribute( 'inert' );
			} else {
				setAttributeIfChanged( slide, 'aria-hidden', 'true' );
				setAttributeIfChanged( slide, 'inert', '' );
			}
			setAttributeIfChanged( slide, 'role', 'group' );
			setAttributeIfChanged( slide, 'aria-roledescription', 'slide' );
			setAttributeIfChanged(
				slide,
				'aria-label',
				sprintfNamed(
					l10n.slideLabel || '%1$d / %2$d',
					index + 1,
					slides.length
				)
			);
		} );
	};

	const onReInit = () => {
		refreshSlides();
		update();
	};

	embla.on( 'select', update );
	embla.on( 'reInit', onReInit );
	embla.on( 'slidesInView', update );
	update();

	return () => {
		embla.off( 'select', update );
		embla.off( 'reInit', onReInit );
		embla.off( 'slidesInView', update );
		originalAttributes.forEach( ( originals, slide ) => {
			managedAttributes.forEach( ( name ) => {
				const original = originals[ name ];
				if ( original.present ) {
					slide.setAttribute( name, original.value );
				} else {
					slide.removeAttribute( name );
				}
			} );
		} );
	};
}

export function setupImageReinitialization( embla, viewport ) {
	const cleanups = [];
	const unsettledImages = new Set(
		Array.from( viewport.querySelectorAll( 'img' ) )
			.filter(
				( image ) =>
					image.closest( '.ollie-carousel-viewport' ) === viewport
			)
			.filter( ( image ) => ! image.complete )
	);
	let active = true;

	unsettledImages.forEach( ( image ) => {
		const onSettled = () => {
			if ( ! active || ! unsettledImages.delete( image ) ) {
				return;
			}
			image.removeEventListener( 'load', onSettled );
			image.removeEventListener( 'error', onSettled );
			if ( unsettledImages.size === 0 ) {
				embla.reInit();
			}
		};
		image.addEventListener( 'load', onSettled );
		image.addEventListener( 'error', onSettled );
		cleanups.push( () => {
			image.removeEventListener( 'load', onSettled );
			image.removeEventListener( 'error', onSettled );
		} );
	} );

	return () => {
		active = false;
		unsettledImages.clear();
		cleanups.forEach( ( cleanup ) => cleanup() );
	};
}

export function setupBoundaryRtl( boundary, isRtl ) {
	const initiallyRtl = boundary.classList.contains( 'ollie-carousel-rtl' );
	boundary.classList.toggle( 'ollie-carousel-rtl', isRtl );

	return () => {
		boundary.classList.toggle( 'ollie-carousel-rtl', initiallyRtl );
	};
}

export function initCarousel( viewport ) {
	if ( viewport._ollie ) {
		// Already tracked: if the slide markup was swapped underneath us,
		// refresh Embla's snapshot instead of silently returning stale state.
		const embla = viewport._ollie.embla;
		const container = viewport.firstElementChild;
		if (
			container &&
			embla.slideNodes().length !== container.children.length
		) {
			embla.reInit();
		}
		return embla;
	}

	const boundary = viewport.closest( '.ollie-carousel-block' );
	if ( ! boundary ) {
		// eslint-disable-next-line no-console
		console.warn(
			'Ollie Carousel: viewport is missing its .ollie-carousel-block ancestor and was not initialized.',
			viewport
		);
		return null;
	}

	const attribute = ( name ) =>
		viewport.getAttribute( `data-ollie-${ name }` );
	const integerAttribute = ( name ) =>
		Number.parseInt( attribute( name ), 10 ) || 0;
	const booleanAttribute = ( name ) => attribute( name ) === 'true';
	const baseSettings = {
		slidesPerView: integerAttribute( 'slides-per-view' ) || 1,
		slidesPerGroup: integerAttribute( 'slides-per-group' ) || 1,
	};
	const breakpoints = parseBreakpoints( attribute( 'breakpoints' ) );
	const minSlideWidth = integerAttribute( 'min-slide-width' );
	const measureSlideGap = () => {
		const slide = viewport.querySelector( '.ollie-carousel-slide' );
		if ( ! slide ) {
			return 0;
		}
		const styles = window.getComputedStyle( slide );
		return (
			Number.parseFloat(
				styles.borderInlineStartWidth || styles.borderLeftWidth
			) || 0
		);
	};
	const capSettings = ( settings ) =>
		capSlidesToMinWidth(
			settings,
			viewport.clientWidth,
			measureSlideGap(),
			minSlideWidth
		);
	let effectiveSettings = capSettings(
		resolveResponsiveSettings( baseSettings, breakpoints, window.innerWidth )
	);
	const speed = integerAttribute( 'speed' );
	const hasAutoplay = booleanAttribute( 'autoplay' );
	const hasFade = booleanAttribute( 'fade' );
	const isRtl =
		booleanAttribute( 'rtl' ) || viewport.getAttribute( 'dir' ) === 'rtl';
	const prefersReducedMotion = window.matchMedia(
		'(prefers-reduced-motion: reduce)'
	).matches;
	const options = {
		loop: booleanAttribute( 'loop' ),
		direction: isRtl ? 'rtl' : 'ltr',
		slidesToScroll: effectiveSettings.slidesPerGroup,
		align: 'start',
		containScroll: 'trimSnaps',
		duration: prefersReducedMotion ? 0 : Math.round( speed / 12 ),
		dragFree: false,
	};

	const createPlugins = ( useFade, slidesPerView ) => {
		const plugins = [];
		if ( useFade ) {
			plugins.push( Fade() );
		} else if ( shouldUseAutoHeight( useFade, slidesPerView ) ) {
			plugins.push( AutoHeight() );
		}
		if ( hasAutoplay && ! prefersReducedMotion ) {
			// Continuous scroll needs a looping track to drift into; without
			// loop (or with fade) it falls back to stepped autoplay.
			const continuousScroll =
				attribute( 'autoplay-style' ) === 'scroll' &&
				options.loop &&
				! useFade;
			if ( continuousScroll ) {
				plugins.push(
					AutoScroll( {
						speed: Number.parseFloat( attribute( 'scroll-speed' ) ) || 2,
						startDelay: 0,
						stopOnInteraction: false,
						stopOnMouseEnter: false,
					} )
				);
			} else {
				plugins.push(
					Autoplay( {
						delay: integerAttribute( 'autoplay-speed' ) || 3000,
						stopOnInteraction: true,
						stopOnMouseEnter: false,
					} )
				);
			}
		}
		return plugins;
	};

	const initialFade = hasFade && effectiveSettings.slidesPerView === 1;
	applySlideStyles( viewport, effectiveSettings.slidesPerView );
	// Slides stretch to equal height by default; auto-height mode needs
	// natural heights so it can animate the viewport per slide.
	viewport.classList.toggle(
		'ollie-carousel-auto-height',
		shouldUseAutoHeight( initialFade, effectiveSettings.slidesPerView )
	);
	const cleanupBoundaryRtl = setupBoundaryRtl( boundary, isRtl );

	const embla = EmblaCarousel(
		viewport,
		options,
		createPlugins( initialFade, effectiveSettings.slidesPerView )
	);
	const cleanups = [ cleanupBoundaryRtl ];

	// Register the destroy handle before the setup sequence runs: if any
	// setup helper throws, initAll's catch can destroy the partial instance
	// (cleanup closes over the cleanups collected so far) instead of leaving
	// an orphaned Embla that a later update() would double-mount over.
	let cleaned = false;
	const cleanup = () => {
		if ( cleaned ) {
			return;
		}
		cleaned = true;
		cleanups.forEach( ( clean ) => clean() );
		viewport._ollie = null;
	};
	embla.on( 'destroy', cleanup );
	viewport._ollie = { embla, destroy: () => embla.destroy() };

	const prevButtons = getBoundaryElements( boundary, '.ollie-carousel-prev' );
	const nextButtons = getBoundaryElements( boundary, '.ollie-carousel-next' );
	if ( prevButtons.length || nextButtons.length ) {
		cleanups.push( setupNavigation( embla, prevButtons, nextButtons ) );
	}
	getBoundaryElements( boundary, '.ollie-carousel-pagination' ).forEach(
		( pagination ) => {
			cleanups.push( setupPagination( embla, pagination ) );
		}
	);
	getBoundaryElements( boundary, '.ollie-carousel-scrollbar' ).forEach(
		( scrollbar ) => {
			cleanups.push( setupScrollbar( embla, scrollbar ) );
		}
	);
	cleanups.push( setupKeyboardNavigation( embla, viewport, isRtl ) );
	cleanups.push(
		setupSlideAccessibility(
			embla,
			viewport,
			() => effectiveSettings.slidesPerView
		)
	);
	cleanups.push( setupImageReinitialization( embla, viewport ) );
	if ( hasAutoplay && ! prefersReducedMotion ) {
		const autoplayState = { userPaused: false };
		cleanups.push( setupAutoplayToggle( embla, boundary, autoplayState ) );
		cleanups.push( setupInteractionPause( embla, autoplayState ) );
		if ( booleanAttribute( 'pause-on-hover' ) ) {
			cleanups.push(
				setupPauseOnHover( embla, viewport, autoplayState )
			);
		}
	} else {
		// No autoplay to control (disabled or reduced motion): a user-placed
		// pause button would be a dead control.
		getBoundaryElements(
			boundary,
			'.ollie-carousel-autoplay-toggle'
		).forEach( ( toggle ) => {
			const wrapper =
				toggle.closest( '.ollie-carousel-nav--autoplay' ) || toggle;
			wrapper.hidden = true;
		} );
	}
	cleanups.push(
		setupResponsiveSettings( {
			embla,
			viewport,
			baseSettings,
			breakpoints,
			capSettings,
			watchResize: minSlideWidth > 0,
			createPlugins,
			initialSettings: effectiveSettings,
			initialFade,
			hasFade,
			onSettingsChange: ( settings ) => {
				effectiveSettings = settings;
			},
		} )
	);

	return embla;
}

function initAll( root = document ) {
	// Fault-isolate each carousel so one broken instance (mangled markup from
	// a minifier, a plugin mutating the DOM, …) cannot prevent every carousel
	// after it in DOM order from initializing.
	root.querySelectorAll( '.ollie-carousel-viewport' ).forEach(
		( viewport ) => {
			try {
				initCarousel( viewport );
			} catch ( error ) {
				// Roll back the partial instance so nothing leaks and a
				// later update() cannot double-mount over it.
				destroyCarousel( viewport );
				// eslint-disable-next-line no-console
				console.error(
					'Ollie Carousel: failed to initialize.',
					viewport,
					error
				);
			}
		}
	);
}

function updateAll( root = document ) {
	root.querySelectorAll( '.ollie-carousel-viewport' ).forEach(
		( viewport ) => {
			try {
				if ( viewport._ollie ) {
					viewport._ollie.embla.reInit();
				} else {
					initCarousel( viewport );
				}
			} catch ( error ) {
				destroyCarousel( viewport );
				// eslint-disable-next-line no-console
				console.error(
					'Ollie Carousel: failed to update.',
					viewport,
					error
				);
			}
		}
	);
}

export function destroyCarousel( viewport ) {
	viewport._ollie?.destroy();
}

function destroyAll( root = document ) {
	root.querySelectorAll( '.ollie-carousel-viewport' ).forEach(
		destroyCarousel
	);
}

/**
 * Automatically destroy carousels whose DOM is removed (AJAX filters,
 * popups, client-side navigation) and re-initialize carousels whose DOM is
 * (re-)inserted — e.g. a modal library re-attaching its content, or
 * PJAX-style navigation injecting new HTML. Without the destroy half, each
 * discarded instance leaks document-level listeners and observers; without
 * the init half, re-attached carousels would stay permanently dead.
 */
function watchForRemovals() {
	if ( typeof window.MutationObserver === 'undefined' || ! document.body ) {
		return;
	}
	const observer = new window.MutationObserver( ( mutations ) => {
		mutations.forEach( ( mutation ) => {
			mutation.removedNodes.forEach( ( node ) => {
				if ( node.nodeType !== 1 ) {
					return;
				}
				if (
					node.matches?.( '.ollie-carousel-viewport' ) &&
					! node.isConnected
				) {
					destroyCarousel( node );
					return;
				}
				node.querySelectorAll?.( '.ollie-carousel-viewport' ).forEach(
					( viewport ) => {
						if ( ! viewport.isConnected ) {
							destroyCarousel( viewport );
						}
					}
				);
			} );
			mutation.addedNodes.forEach( ( node ) => {
				if ( node.nodeType !== 1 || ! node.isConnected ) {
					return;
				}
				// initCarousel is idempotent via the _ollie guard, so
				// re-observed moves are harmless. Only complete carousel
				// markup (viewport with its slides container) qualifies —
				// bare or partial viewports would just throw in Embla.
				const viewports = node.matches?.(
					'.ollie-carousel-viewport'
				)
					? [ node ]
					: Array.from(
							node.querySelectorAll?.(
								'.ollie-carousel-viewport'
							) || []
					  );
				viewports.forEach( ( viewport ) => {
					if (
						viewport.querySelector(
							':scope > .ollie-carousel-container'
						)
					) {
						initAll( { querySelectorAll: () => [ viewport ] } );
					}
				} );
			} );
		} );
	} );
	observer.observe( document.body, { childList: true, subtree: true } );
}

window.OllieCarousel = {
	init: initAll,
	update: updateAll,
	destroy: destroyAll,
};

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', () => {
		initAll();
		watchForRemovals();
	} );
} else {
	initAll();
	watchForRemovals();
}
